/**
 * Provision Timings Hook
 *
 * Fetches the per-step provisioning timing breakdown for a sandbox instance's
 * most recent provision/rebuild run. Powers the "Provision timing" card on the
 * sandbox detail page.
 *
 * @module features/sandboxes/hooks/useProvisionTimings
 */

import { useQuery } from '@tanstack/react-query';
import type { ProvisionTimingSummary } from '@chatsdet/types';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';

export type ProvisionTiming = {
  id: string;
  runId: string;
  sequence: number;
  /** `phase` | `health_stage` | `deploy_step` | `container` */
  kind: string;
  /** e.g. `phase4_deploy`, `app_up`, `web-build`, `total` */
  step: string;
  durationMs: number;
  /** Step-scoped terminal outcome; `cache_restore` rows carry `hit` | `prime` | `miss`. */
  outcome?: string | null;
  /** Wall-clock start (ISO); timeline anchor for the Gantt view. Null on older runs. */
  startedAt?: string | null;
  /** Launch source: `stopped_pool` | `warm_pool` | `cold_fresh`. Superadmin-only. */
  warmCold?: string | null;
  bakedAmi?: boolean | null;
  createdAt: string;
};

/**
 * What the endpoint returns, which depends on the caller.
 *
 * The API sends per-step `timings` only to superadmins; everyone else gets a
 * ready/build/total `summary`. The two are mutually exclusive by construction,
 * so the component picks its view from which one arrived rather than from a
 * separate permission check that could drift out of sync with the server.
 */
export type ProvisionTimingsResponse = {
  timings: ProvisionTiming[] | null;
  summary: ProvisionTimingSummary | null;
};

export async function fetchProvisionTimings(
  instanceId: string
): Promise<ProvisionTimingsResponse> {
  const res = await fetch(`${API_URL}/org/environments/instances/${instanceId}/timings`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch provision timings');
  const json = (await res.json()) as {
    data?: { timings?: ProvisionTiming[]; summary?: ProvisionTimingSummary | null };
  };
  return {
    timings: json?.data?.timings ?? null,
    summary: json?.data?.summary ?? null,
  };
}

export function useProvisionTimings(instanceId: string | undefined) {
  return useQuery({
    queryKey: ['provision-timings', instanceId],
    enabled: Boolean(instanceId),
    queryFn: () => fetchProvisionTimings(instanceId!),
  });
}
